/**
 * Input component
 *
 * Optional props:
 * inlineBlock : boolean - display inline-block
 * width : string - width of input (with unit)
 * auto : boolean - auto width
 * height : string - height of input (with unit)
 * noBorder : boolean - disable border
 * underline : boolean - underline-only border style
 * success : boolean - success look
 * warning : boolean - warning look
 * error : boolean - error look
 * suffix : string - add suffix to input element
 *
 * Examples of component:
 * <Input width="100px" placeholder="Standard input" />
 * <Input error placeholder="Error style" />
 *
 */

import styled from 'styled-components';
import { colors } from '../../variables';
import { InputMessage } from '../';
import React from 'react';
import approveIcon from '../../../images/approve.svg';
import errorIcon from '../../../images/error.svg';

const getWidth = props => {
	if (props.width) return props.width;
	if (props.auto) return 'auto';
	return '100%';
};

const getHeight = props => {
	if (props.height) return props.height;
	if (props.type === 'date') return '40px';
	return 'unset';
};

const getBorderColor = (props, state = false) => {
	if (props.success) return colors.green.eco;
	if (props.warning) return colors.orange.spreader;
	if (props.error) return colors.red.warning;
	if (state === 'focus') return colors.blue.brommaLight;

	return '#96a0ad';
};

const getIcon = props => {
	if (props.success) return approveIcon;
	if (props.error) return errorIcon;
	return '';
};

const StyledInput = styled.input`
	outline: 0;
	padding: ${props => (props.underline ? '0 0 0 0' : '10px')};
	border-radius: ${props => (props.underline ? '0' : '4px')};
	border-style: solid;
	font-size: ${props => (props.underline ? '16px' : 'inherit')};
	${props => {
		if (props.noBorder) {
			return 'border-width: 0;';
		}
		if (props.underline) {
			return `
				border-top: 0;
				border-left: 0;
				border-right: 0;
				border-bottom: 2px solid ${getBorderColor(props)};
				background: transparent;
				line-height: 1.5;
			`;
		}
		return `
			border-width: 1px;
			border-color: ${getBorderColor(props)};
		`;
	}} display: ${props => (props.inlineBlock ? 'inline-block' : 'block')};
	width: ${props => getWidth(props)};
	position: relative;
	padding-right: ${props => {
		if (props.underline) return '0';
		return props.hasIcon ? '45px' : '5px';
	}};
	height: ${props => {
		if (props.underline) return 'auto';
		return getHeight(props);
	}};

	&:focus {
		${props => {
			if (props.underline) {
				return `
					border-bottom-color: ${getBorderColor(props, 'focus')};
				`;
			}
			return `border-color: ${getBorderColor(props, 'focus')};`;
		}};
	}

	&:disabled {
		background: ${colors.grey.lightest};
		cursor: disabled;
	}
`;

const InputContainer = styled.div`
	position: relative;
`;

const Suffix = styled.span`
	display: block;
	position: absolute;
	top: 0;
	background: ${colors.blue.brommaDark};
	padding: 0 10px;
	line-height: ${props =>
		getHeight(props) === 'unset' ? '40px' : getHeight(props)};
	right: 0;
	color: #fff;
`;

const Icon = styled.span`
	display: ${props => (props.hasIcon ? 'block' : 'none')};
	position: absolute;
	right: 15px;
	width: 20px;
	height: 20px;
	top: 50%;
	transform: translateY(-50%);
	background: url(${props => getIcon(props)});
	background-size: contain;
	background-position: center;
	background-repeat: no-repeat;
`;

const Input = props => {
	const { className, input, type, id, suffix = false, ...rest } = props;

	const meta = props.meta || {};
	const valid = meta.valid || false;
	const invalid = meta.invalid || false;
	const submitFailed = meta.submitFailed || false;
	const error = meta.error || false;

	const hasIcon = (valid || invalid) && submitFailed;
	const hasError = meta.invalid && meta.submitFailed;

	return (
		<div className={className}>
			<InputContainer>
				<StyledInput
					{...rest}
					{...input}
					id={id}
					hasIcon={hasIcon}
					error={hasError}
					type={type || 'text'}
				/>

				<Icon {...rest} hasIcon={hasIcon} error={hasError} />
				{suffix && <Suffix>{suffix}</Suffix>}
			</InputContainer>

			{submitFailed && error && <InputMessage>{error}</InputMessage>}
		</div>
	);
};

export default Input;



// WEBPACK FOOTER //
// ./src/components/Common/Input/Input.js